import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  fetchApprovals,
  fetchDesignPreviews,
  fetchPostPreviews,
  decideApproval,
  decideApprovalModified,
  splitApprovals,
} from '@/lib/approvalsData';
import ApprovalCard, { formatWhen } from '@/components/dashboard/ApprovalCard';

/* K.O. Command approval inbox (Phase 5). Every card is a decision Milli
 * refused to make on her own — the tools stop and file a kc_approvals row
 * whenever an action crosses the authority line (spends money, contacts a
 * client, binds the business). Approve/Reject calls the audited
 * kc_approval_decide RPC; this page never writes tables directly. */

const STATUS_TONE = {
  approved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
  modified: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  expired: 'bg-muted text-muted-foreground border-border',
};

// The pending card itself lives in components/dashboard/ApprovalCard.js so
// Milli's console draws the very same card.

export default function ApprovalsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [previews, setPreviews] = useState({});
  const [postPreviews, setPostPreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // { row, decision } while the confirm dialog is open; id while the RPC runs.
  const [confirming, setConfirming] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const fetched = await fetchApprovals();
        setRows(fetched);
        // Design cards (069) carry previews; fetched separately so a signing
        // hiccup never blanks the inbox.
        const pendingIds = fetched
          .filter((r) => r?.status === 'pending')
          .map((r) => r.id);
        const [design, posts] = await Promise.all([
          fetchDesignPreviews(pendingIds),
          fetchPostPreviews(pendingIds),
        ]);
        setPreviews(design);
        setPostPreviews(posts);
      } catch (e) {
        setError(e?.message || t('approvals.loadError'));
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const { pending, decided } = useMemo(() => splitApprovals(rows), [rows]);

  const confirmDecision = async () => {
    const { row, decision, edits } = confirming || {};
    setConfirming(null);
    if (!row) return;
    setBusyId(row.id);
    setError(null);
    try {
      if (decision === 'modified') await decideApprovalModified(row.id, edits);
      else await decideApproval(row.id, decision);
      // Re-fetch rather than patch locally: decided_at/decided_by are set
      // server-side, and a stale card that still shows buttons is worse
      // than a spinner.
      await load({ silent: true });
    } catch (e) {
      setError(e?.message || t('approvals.decideError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="approvals-page">
      <PageHeader
        eyebrow={t('approvals.eyebrow')}
        title={t('approvals.title')}
        subtitle={t('approvals.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="approvals-refresh"
          >
            <RefreshCw
              size={14}
              className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
            />
            {t('approvals.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="approvals-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            {pending.length === 0 ? (
              <Card data-testid="approvals-empty">
                <CardContent className="py-10 text-center text-muted-foreground">
                  <ShieldCheck className="mx-auto mb-3 h-8 w-8 opacity-60" />
                  <p className="text-sm">{t('approvals.empty')}</p>
                </CardContent>
              </Card>
            ) : (
              pending.map((row) => (
                <ApprovalCard
                  key={row.id}
                  row={row}
                  previews={previews[row.id]}
                  postPreview={postPreviews[row.id]}
                  busy={busyId === row.id}
                  onDecide={(r, decision, edits) => setConfirming({ row: r, decision, edits })}
                  t={t}
                />
              ))
            )}
          </div>

          <div>
            <Card data-testid="approvals-history">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-[0.14em] text-muted-foreground">
                  {t('approvals.historyTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {decided.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('approvals.historyEmpty')}
                  </p>
                ) : (
                  decided.slice(0, 20).map((row) => (
                    <div key={row.id} className="text-sm space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            STATUS_TONE[row.status] || STATUS_TONE.expired
                          }
                        >
                          {t(`approvals.status.${row.status}`, row.status)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatWhen(row.decided_at)}
                        </span>
                      </div>
                      <p className="text-foreground leading-snug">
                        {row.decision_needed}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <AlertDialog
        open={!!confirming}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <AlertDialogContent data-testid="approvals-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.decision === 'approved'
                ? t('approvals.confirmApproveTitle')
                : confirming?.decision === 'modified'
                  ? t('approvals.confirmModifiedTitle')
                  : t('approvals.confirmRejectTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.row?.decision_needed}
              {confirming?.decision === 'approved'
                ? `. ${t('approvals.confirmApproveBody')}`
                : confirming?.decision === 'modified'
                  ? `. ${t('approvals.confirmModifiedBody')}`
                  : `. ${t('approvals.confirmRejectBody')}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('approvals.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDecision}
              className={
                confirming?.decision === 'rejected'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }
              data-testid="approvals-confirm-action"
            >
              {confirming?.decision === 'approved'
                ? t('approvals.approve')
                : confirming?.decision === 'modified'
                  ? t('approvals.approveEdited')
                  : t('approvals.reject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
